import React, { FC } from 'react';
import { Modal, Spin } from 'antd';
import { IncidentEmailPreview } from 'core/incidents/types';
import './emailPreviewModal.scss';

interface EmailPreviewModalProps {
    visible: boolean;
    onClose: () => void;
    isLoading: boolean;
    preview: IncidentEmailPreview | null;
    // What the reader is about to submit, e.g. "diary entry" / "update".
    // eslint-disable-next-line react/require-default-props
    sendsOn?: string;
}

/**
 * Read-only preview of the distribution email that WILL be sent when a change is
 * saved with "send to distribution" ticked — shared by the diary entry and the
 * incident edit. The email HTML is rendered in a sandboxed iframe so its own
 * styles can't leak into the app, and the resolved recipient list is shown so
 * the sender can catch mistakes before the update goes out to everyone on the
 * distribution list.
 */
const EmailPreviewModal: FC<EmailPreviewModalProps> = ({
    visible,
    onClose,
    isLoading,
    preview,
    sendsOn = 'diary entry',
}) => (
    <Modal
        title="Email Preview"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={760}
        centered
        wrapClassName="email-preview-modal">
        {isLoading && (
            <div className="preview-loading">
                <Spin size="large" />
            </div>
        )}

        {!isLoading && preview && (
            <div className="preview-body">
                <p className="preview-note">
                    {`This is a preview only. The email is sent when you submit the ${sendsOn}.`}
                </p>

                <div className="preview-meta">
                    <div className="preview-meta-row">
                        <span className="preview-meta-label">Subject:</span>
                        <span className="preview-meta-value">{preview.subject}</span>
                    </div>
                    <div className="preview-meta-row">
                        <span className="preview-meta-label">
                            Recipients ({preview.recipients.length}):
                        </span>
                        <span className="preview-meta-value">
                            {preview.recipients.length > 0
                                ? preview.recipients.join(', ')
                                : 'No recipients, no distribution list is configured for this incident, so no email will be sent.'}
                        </span>
                    </div>
                </div>

                {/*
                  Trust boundary: preview.html is first-party HTML rendered by our
                  own Blade email templates, with all user-supplied data ({{ }}
                  auto-escaped). It is NOT arbitrary third-party markup.
                  Defence in depth: `sandbox=""` (the strictest value) disables
                  scripts, same-origin access, forms, and navigation, so injected
                  markup cannot execute or reach the parent; `no-referrer` avoids
                  leaking the app URL to any external asset the email references.
                */}
                <iframe
                    className="preview-frame"
                    title="Email preview"
                    sandbox=""
                    referrerPolicy="no-referrer"
                    srcDoc={preview.html} />
            </div>
        )}

        <div className="preview-buttons">
            <button type="button" className="preview-close-button" onClick={onClose}>
                Close
            </button>
        </div>
    </Modal>
);

export default EmailPreviewModal;
